import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Purchases, LogLevel } from '@revenuecat/purchases-js';
import { useAuth } from './AuthContext';
import { apiCall, API_ROUTES } from '../config/api';
import { trackPurchase as trackMetaPurchase } from '../utils/metaTracking';
import { trackPurchase as trackTikTokPurchase } from '../utils/tiktokTracking';

// RevenueCat configuration - using the same setup as iOS app
const REVENUECAT_PUBLIC_KEY = process.env.REACT_APP_REVENUECAT_PUBLIC_KEY;
const WEB_OFFERING_IDENTIFIER = 'webq4';

// Web billing package IDs in the webq4 offering.
const TARGET_WEB_PACKAGE_IDS = [
  '$rc_annual',
  '$rc_monthly'
];

const isTargetWebPackage = (pkg) => {
  const packageId = pkg?.identifier;
  return TARGET_WEB_PACKAGE_IDS.includes(packageId);
};

const selectWebOffering = (offerings) => {
  if (offerings?.all?.[WEB_OFFERING_IDENTIFIER]) {
    return {
      id: WEB_OFFERING_IDENTIFIER,
      offering: offerings.all[WEB_OFFERING_IDENTIFIER]
    };
  }

  if (offerings?.current) {
    return {
      id: offerings.current.identifier || 'current',
      offering: offerings.current
    };
  }

  return null;
};

// Mock offerings for development testing - matching iOS app structure
const MOCK_OFFERINGS = {
  current: {
    identifier: 'default3',
    displayName: 'Default',
    availablePackages: [
      {
        identifier: '$rc_monthly',
        packageType: 'MONTHLY',
        product: {
          identifier: 'sb_999_1m_1w0',
          title: 'Monthly Subscription',
          description: 'Full access to Lecha for one month',
          price: 999, // Price in cents
          priceString: '$9.99/month',
          currencyCode: 'USD',
        }
      },
      {
        identifier: '$rc_annual',
        packageType: 'ANNUAL',
        product: {
          identifier: 'sb_5999_1y_1w0',
          title: 'Annual Subscription',
          description: 'Full access to Lecha for one year (save 40%)',
          price: 5999, // Price in cents
          priceString: '$59.99/year',
          currencyCode: 'USD',
        }
      },
      {
        identifier: 'AnnualDiscount',
        packageType: 'ANNUAL',
        product: {
          identifier: 'annual_discount_50',
          title: 'Annual Subscription (50% Off)',
          description: 'Full access to Lecha for one year - Special discount offer',
          price: 2999, // Price in cents
          priceString: '$29.99/year',
          currencyCode: 'USD',
        }
      }
    ]
  }
};

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState('loading');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [packages, setPackages] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isContextReady, setIsContextReady] = useState(false);
  const [billingSource, setBillingSource] = useState('none');
  const [checkoutProvider, setCheckoutProvider] = useState('revenuecat_web_billing');
  const hasInitializedRef = useRef(false);
  const activeAppUserIdRef = useRef(null);
  const packagesLoadPromiseRef = useRef(null);
  
  // Freemium state
  const [userUsage, setUserUsage] = useState({
    freeNotesUsed: 0,
    freeNotesLimit: 2,
    canCreateNote: true,
    hasReachedLimit: false
  });

  const getPlanFromPackage = useCallback((pkg, packageIdentifier) => {
    const candidates = [
      pkg?.packageType,
      pkg?.identifier,
      pkg?.product?.identifier,
      pkg?.rcPackage?.packageType,
      pkg?.rcPackage?.identifier,
      packageIdentifier,
    ]
      .filter(Boolean)
      .map(value => String(value).toLowerCase());

    if (candidates.some(value => value.includes('annual') || value.includes('year') || value.includes('christmas') || value.includes('holiday'))) {
      return 'annual';
    }

    if (candidates.some(value => value.includes('month'))) {
      return 'monthly';
    }

    return null;
  }, []);

  const sleep = useCallback((ms) => new Promise(resolve => setTimeout(resolve, ms)), []);

  // Initialize RevenueCat
  useEffect(() => {
    if (authLoading || hasInitializedRef.current) {
      return undefined;
    }

    hasInitializedRef.current = true;

    // Clear any existing development bypass
    if (process.env.NODE_ENV === 'development') {
      localStorage.removeItem('dev_subscribed');
    }

    const initializeRevenueCat = async () => {
      try {
        console.log('Initializing RevenueCat with key:', REVENUECAT_PUBLIC_KEY);
        
        // For development, enable debug logs
        if (process.env.NODE_ENV === 'development') {
          Purchases.setLogLevel(LogLevel.DEBUG);
        }
        
        // Configure RevenueCat with timeout
        const userIdToUse = currentUser?.uid || Purchases.generateRevenueCatAnonymousAppUserId();
        console.log('Configuring with user ID:', userIdToUse);
        
        const configPromise = Purchases.configure(REVENUECAT_PUBLIC_KEY, userIdToUse);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('RevenueCat configuration timeout')), 10000)
        );
        
        const purchasesInstance = await Promise.race([configPromise, timeoutPromise]);
        console.log('RevenueCat initialized successfully', purchasesInstance);
        activeAppUserIdRef.current = userIdToUse;

        // Mark the context ready immediately after configure.
        // Offerings and subscription state can load in the background.
        setIsContextReady(true);
        setIsLoading(false);

        loadPackages().catch((error) => {
          console.error('Background package load failed:', error);
        });
        
      } catch (error) {
        console.error('Error initializing RevenueCat:', error);
        // Use mock data for development
        console.log('Using mock offerings for development');
        setPackages(MOCK_OFFERINGS.current.availablePackages);
        
        // Set subscription status to inactive so app doesn't hang
        setIsSubscribed(false);
        setSubscriptionStatus('inactive');
        setIsContextReady(true);
        setIsLoading(false);
      }
    };

    // Add a timeout fallback in case everything fails
    const fallbackTimeout = setTimeout(() => {
      console.warn('RevenueCat initialization taking too long, using fallback');
      setPackages(MOCK_OFFERINGS.current.availablePackages);
      setIsSubscribed(false);
      setSubscriptionStatus('inactive');
      setIsContextReady(true);
      setIsLoading(false);
    }, 15000);

    initializeRevenueCat().finally(() => {
      clearTimeout(fallbackTimeout);
    });

    return () => {
      clearTimeout(fallbackTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, currentUser?.uid]); // Wait for auth to settle before first configure

  // Check user usage status from backend
  const checkUsageStatus = useCallback(async () => {
    try {
      if (!currentUser) return null;
      
      const usageData = await apiCall(API_ROUTES.USER_USAGE);
      console.log('Usage data:', usageData);
      
      setUserUsage({
        freeNotesUsed: usageData.freeNotesUsed,
        freeNotesLimit: usageData.freeNotesLimit,
        canCreateNote: usageData.canCreateNote,
        hasReachedLimit: usageData.hasReachedLimit
      });
      setBillingSource(usageData.billingSource || 'none');
      setCheckoutProvider(usageData.checkoutProvider || 'revenuecat_web_billing');
      
      // Update subscription status from backend
      if (usageData.subscriptionStatus === 'active' || usageData.subscriptionStatus === 'trialing') {
        setIsSubscribed(true);
        setSubscriptionStatus('active');
      } else {
        setIsSubscribed(false);
        setSubscriptionStatus('inactive');
      }

      return usageData;
    } catch (error) {
      console.error('Error checking usage status:', error);
      return null;
    }
  }, [currentUser]);

  const checkSubscriptionStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // First check usage status from backend
      const usageData = await checkUsageStatus();
      const resolvedBillingSource = usageData?.billingSource || 'none';
      
      // For development, check if we want to bypass the paywall (but don't auto-enable)
      // Commented out to test freemium flow properly
      // if (process.env.NODE_ENV === 'development' && localStorage.getItem('dev_subscribed') === 'true') {
      //   console.log('Development mode: Subscription bypass enabled');
      //   setIsSubscribed(true);
      //   setSubscriptionStatus('active');
      //   setIsLoading(false);
      //   return;
      // }

      // Check if RevenueCat is configured before trying to use it
      if (!Purchases.isConfigured()) {
        console.log('RevenueCat not configured, using backend status');
        setIsLoading(false);
        return;
      }

      // Get customer info from RevenueCat
      const customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      setCustomerInfo(customerInfo);
      
      // Check if user has active entitlements
      const hasActiveSubscription = Object.keys(customerInfo.entitlements.active).length > 0;
      
      if (hasActiveSubscription) {
        setIsSubscribed(true);
        setSubscriptionStatus('active');

        try {
          const subscriptionUpdatePayload = { isSubscribed: true };
          if (resolvedBillingSource === 'stripe') {
            subscriptionUpdatePayload.billingSource = 'stripe';
          }

          await apiCall(API_ROUTES.UPDATE_SUBSCRIPTION, {
            method: 'POST',
            body: JSON.stringify(subscriptionUpdatePayload)
          });
        } catch (syncError) {
          console.error('Error syncing positive subscription status with backend:', syncError);
        }
      } else if (resolvedBillingSource !== 'stripe') {
        setIsSubscribed(false);
        setSubscriptionStatus('inactive');
      }
      
      console.log('Subscription status:', hasActiveSubscription ? 'active' : 'inactive');
      console.log('Active entitlements:', Object.keys(customerInfo.entitlements.active));
      
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setIsSubscribed(false);
      setSubscriptionStatus('inactive'); // Changed from 'error' to 'inactive' to show paywall
    } finally {
      setIsLoading(false);
    }
  }, [checkUsageStatus]);

  const loadPackages = useCallback(async () => {
    if (packagesLoadPromiseRef.current) {
      return packagesLoadPromiseRef.current;
    }

    packagesLoadPromiseRef.current = (async () => {
    try {
      console.log('Loading packages from RevenueCat...');
      
      // Check if RevenueCat is configured before trying to use it
      if (!Purchases.isConfigured()) {
        console.log('RevenueCat not configured, using mock packages');
        setPackages(MOCK_OFFERINGS.current.availablePackages);
        return;
      }
      
      const offerings = await Purchases.getSharedInstance().getOfferings();
      
      // Also try fetching with explicit currency to see if that loads prices
      let offeringsWithCurrency = null;
      try {
        offeringsWithCurrency = await Purchases.getSharedInstance().getOfferings({
          currency: 'USD'
        });
        console.log('Offerings with USD currency:', offeringsWithCurrency);
      } catch (currencyError) {
        console.log('Could not fetch offerings with currency:', currencyError);
      }
      
      // Use currency-specific offerings if available and has pricing, otherwise use default
      const offeringsToUse = offeringsWithCurrency || offerings;
      
      const selected = selectWebOffering(offeringsToUse);
      if (!selected?.offering) {
        console.log('No offering available from RevenueCat, using mock packages');
        setPackages(MOCK_OFFERINGS.current.availablePackages);
        return;
      }

      const { id: selectedOfferingId, offering: selectedOffering } = selected;
      console.log('All offerings (final):', Object.keys(offeringsToUse.all || {}));
      console.log('Selected offering for web billing:', selectedOfferingId);
      console.log('Target package IDs we are looking for:', TARGET_WEB_PACKAGE_IDS);

      const selectedPackages = selectedOffering.availablePackages
        .filter(isTargetWebPackage)
        .map(pkg => ({
          identifier: pkg.identifier,
          product: {
            identifier: pkg.webBillingProduct?.identifier || pkg.identifier,
            title: pkg.webBillingProduct?.title || `Product ${pkg.identifier}`,
            description: pkg.webBillingProduct?.description || '',
            // Extract price from currentPrice property (amount is in cents, convert to dollars)
            price: pkg.webBillingProduct?.currentPrice?.amount ||
                   pkg.webBillingProduct?.currentPrice?.amountMicros / 1000000 ||
                   pkg.webBillingProduct?.price ||
                   0,
            priceString: pkg.webBillingProduct?.currentPrice?.formattedPrice ||
                        pkg.webBillingProduct?.priceString ||
                        '$0.00',
            currencyCode: pkg.webBillingProduct?.currentPrice?.currency ||
                         pkg.webBillingProduct?.currencyCode ||
                         'USD',
          },
          packageType: pkg.packageType,
          // Store the original package for purchase
          rcPackage: pkg,
          // Store offering info for reference
          offeringId: selectedOfferingId
        }));

      if (selectedPackages.length > 0) {
        setPackages(selectedPackages);
        console.log('Loaded packages from selected offering:', selectedPackages);
        
        // Debug: Log specific packages we're looking for
        const rcAnnual = selectedPackages.find(pkg => pkg.identifier === '$rc_annual');
        const rcMonthly = selectedPackages.find(pkg => pkg.identifier === '$rc_monthly');
        console.log('Found annual package ($rc_annual):', rcAnnual);
        console.log('Found monthly package ($rc_monthly):', rcMonthly);
        
      } else {
        console.log('No target packages found in selected offering, using mock packages for development');
        setPackages(MOCK_OFFERINGS.current.availablePackages);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      // Set mock packages as fallback
      console.log('Using mock packages as fallback');
      setPackages(MOCK_OFFERINGS.current.availablePackages);
    } finally {
      packagesLoadPromiseRef.current = null;
    }
    })();

    return packagesLoadPromiseRef.current;
  }, []);

  // Login user when auth state changes
  useEffect(() => {
    const loginUser = async () => {
      if (currentUser?.uid) {
        try {
          if (!Purchases.isConfigured()) {
            console.log('RevenueCat not configured yet, skipping user login');
            return;
          }

          if (activeAppUserIdRef.current !== currentUser.uid) {
            await Purchases.getSharedInstance().changeUser(currentUser.uid);
            activeAppUserIdRef.current = currentUser.uid;
            console.log('User logged in to RevenueCat:', currentUser.uid);
          }

          if (packages.length === 0) {
            loadPackages().catch((loadError) => {
              console.error('Error loading packages after login:', loadError);
            });
          }

          await checkSubscriptionStatus();
        } catch (error) {
          console.error('Error logging in user to RevenueCat:', error);
        }
      } else {
        try {
          if (!Purchases.isConfigured()) {
            console.log('RevenueCat not configured yet, skipping user logout');
            return;
          }

          if (!activeAppUserIdRef.current || !String(activeAppUserIdRef.current).startsWith('$RCAnonymousID:')) {
            const anonymousId = Purchases.generateRevenueCatAnonymousAppUserId();
            await Purchases.getSharedInstance().changeUser(anonymousId);
            activeAppUserIdRef.current = anonymousId;
          }
          setIsSubscribed(false);
          setSubscriptionStatus('inactive');
          setCustomerInfo(null);
          setBillingSource('none');
        } catch (error) {
          console.error('Error logging out from RevenueCat:', error);
        }
      }
    };

    if (isContextReady) {
      loginUser();
    }
  }, [currentUser, isContextReady, packages.length, loadPackages, checkSubscriptionStatus]);

  const purchasePackage = async (packageIdentifier) => {
    try {
      console.log('Starting purchase for package:', packageIdentifier);
      
      // Find the package
      const packageToPurchase = packages.find(pkg => pkg.identifier === packageIdentifier);
      if (!packageToPurchase) {
        console.error('Package not found. Available packages:', packages.map(p => p.identifier));
        throw new Error(`Package '${packageIdentifier}' not found. Available: ${packages.map(p => p.identifier).join(', ')}`);
      }

      console.log('Package to purchase:', packageToPurchase);

      if (checkoutProvider === 'stripe_checkout') {
        const plan = getPlanFromPackage(packageToPurchase, packageIdentifier);
        if (!plan) {
          throw new Error('Could not determine billing plan for Stripe checkout.');
        }

        const checkout = await apiCall(API_ROUTES.CREATE_CHECKOUT, {
          method: 'POST',
          body: JSON.stringify({ plan })
        });

        if (!checkout?.url) {
          throw new Error('Stripe checkout did not return a redirect URL.');
        }

        window.location.assign(checkout.url);
        return { success: true, redirected: true, sessionId: checkout.sessionId };
      }

      // Try real RevenueCat purchase first
      try {
        console.log('Attempting RevenueCat purchase...');
        
        // For web, RevenueCat uses purchase({ rcPackage: package })
        // If we have the real RevenueCat package, use it; otherwise it's mock data
        if (packageToPurchase.rcPackage) {
          const { customerInfo } = await Purchases.getSharedInstance().purchase({
            rcPackage: packageToPurchase.rcPackage
          });
          
          // Update subscription status
          setCustomerInfo(customerInfo);
          const hasActiveSubscription = Object.keys(customerInfo.entitlements.active).length > 0;
          setIsSubscribed(hasActiveSubscription);
          setSubscriptionStatus(hasActiveSubscription ? 'active' : 'inactive');
          
          // Track purchase with Meta Pixel and TikTok Pixel
          console.log(
            '%c[SubscriptionContext] 💰 Tracking purchase (Package)...',
            'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
          );
          try {
            const product = packageToPurchase.product;
            // Price from packageToPurchase.product.price is stored as extracted from RevenueCat
            // Based on loadPackages, it might be in smallest currency unit (cents) or already converted
            // Check if we need to convert: if price looks like cents (> 100 for typical subscriptions), convert
            // Otherwise, assume it's already in dollars
            let amountInDollars = product.price;
            if (product.price > 100 && product.price < 100000) {
              // Likely in cents (e.g., 999 = $9.99, 5999 = $59.99)
              amountInDollars = product.price / 100;
            }
            
            const currency = product.currencyCode || 'USD';
            const productId = product.identifier;
            
            // Extract transaction ID from customerInfo
            // RevenueCat web SDK: check for transaction ID in active subscriptions or use product ID as fallback
            const activeSubscriptions = Object.keys(customerInfo?.activeSubscriptions || {});
            const transactionId = activeSubscriptions[0] || customerInfo?.latestTransactionId || productId;
            
            const purchaseData = {
              amount: amountInDollars,
              currency: currency,
              productId: productId,
              transactionId: transactionId,
              contentName: product.title || productId,
              contentCategory: 'subscription',
              email: currentUser?.email,
              externalId: currentUser?.uid
            };
            
            await trackMetaPurchase(purchaseData);
            await trackTikTokPurchase(purchaseData);
          } catch (trackError) {
            console.error('[SubscriptionContext] Error tracking purchase:', trackError);
          }
          
          console.log('Purchase completed successfully');
          return { success: true, customerInfo };
        } else {
          // This is mock data, throw error to fall back to simulation
          throw new Error('Mock package - no real RevenueCat package available');
        }
        
      } catch (purchaseError) {
        console.error('RevenueCat purchase failed:', purchaseError);
        
        // In development, fall back to simulation if real purchase fails
        if (process.env.NODE_ENV === 'development') {
          console.log('Development mode: Simulating successful purchase as fallback');
          localStorage.setItem('dev_subscribed', 'false');
          setIsSubscribed(true);
          setSubscriptionStatus('active');
          return { success: true, simulated: true };
        }
        
        // Re-throw the error for production
        throw purchaseError;
      }
      
    } catch (error) {
      console.error('Purchase error:', error);
      
      if (error.errorCode === 'UserCancelledError') {
        throw new Error('Purchase was cancelled');
      } else {
        throw new Error('Purchase failed: ' + error.message);
      }
    }
  };

  const restorePurchases = async () => {
    try {
      if (billingSource !== 'revenuecat_web_billing') {
        return false;
      }

      console.log('Restoring purchases...');
      const customerInfo = await Purchases.restorePurchases();
      setCustomerInfo(customerInfo);
      
      const hasActiveSubscription = Object.keys(customerInfo.entitlements.active).length > 0;
      setIsSubscribed(hasActiveSubscription);
      setSubscriptionStatus(hasActiveSubscription ? 'active' : 'inactive');
      
      console.log('Purchases restored, active:', hasActiveSubscription);
      return hasActiveSubscription;
    } catch (error) {
      console.error('Error restoring purchases:', error);
      throw error;
    }
  };

  const verifyStripeCheckoutSession = useCallback(async (sessionId) => {
    if (!sessionId) {
      throw new Error('Missing Stripe session id');
    }

    try {
      setIsLoading(true);
      const result = await apiCall(API_ROUTES.VERIFY_CHECKOUT_SESSION(sessionId));

      setBillingSource('stripe');
      await checkUsageStatus();

      if (result?.subscriptionStatus === 'active' || result?.subscriptionStatus === 'trialing') {
        setIsSubscribed(true);
        setSubscriptionStatus('active');
      }

      if (Purchases.isConfigured()) {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          const latestCustomerInfo = await Purchases.getSharedInstance().getCustomerInfo();
          setCustomerInfo(latestCustomerInfo);

          const hasActiveSubscription = Object.keys(latestCustomerInfo.entitlements.active).length > 0;
          if (hasActiveSubscription) {
            setIsSubscribed(true);
            setSubscriptionStatus('active');
            break;
          }

          await sleep(1500);
        }
      }

      return result;
    } finally {
      setIsLoading(false);
    }
  }, [checkUsageStatus, sleep]);

  const openBillingPortal = useCallback(async () => {
    const portal = await apiCall(API_ROUTES.CUSTOMER_PORTAL, {
      method: 'POST'
    });

    if (!portal?.url) {
      throw new Error('Stripe billing portal did not return a redirect URL.');
    }

    window.location.assign(portal.url);
  }, []);

  // Function to refresh usage status
  const refreshUsage = useCallback(async () => {
    await checkUsageStatus();
  }, [checkUsageStatus]);

  const value = {
    isSubscribed,
    subscriptionStatus,
    packages,
    customerInfo,
    isLoading,
    isContextReady,
    userUsage,
    billingSource,
    checkoutProvider,
    checkSubscriptionStatus,
    checkUsageStatus,
    refreshUsage,
    purchasePackage,
    restorePurchases,
    verifyStripeCheckoutSession,
    openBillingPortal
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
